import {
	Dialog,
	DialogActions,
	Button,
	Box,
	Stack,
	Typography,
	Chip,
	Switch,
	IconButton,
	Divider,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TableRow,
	Paper,
} from "@mui/material";
import { AddRounded, DeleteRounded, EditRounded } from "@mui/icons-material";
import { useEffect, useMemo, useState } from "react";
import { InfoOutlined } from "@mui/icons-material";
import { LightTooltip } from "components/PimDashboard/CommonComponents/CustomToolTip/CustomToolTip";
import { grey } from "@mui/material/colors";

import RenderInputField from "components/ReusableComponents/RenderInputField/RenderInputField";
import { getColDefaultValue } from "components/PimDashboard/data";
import { GlobalState } from "context/GlobalContext";
import { arrType, inputTypes, selectOptionsKeyName, statusOptions } from "data/globalData";
import {
	deriveSupplierDesignerApprovalFromSamples,
	trimCategoryColumn,
	trimCurrencyDropdowns,
	trimsMasterColumnsObj,
	trimsSupplierColumnsObj,
	trimDesignerApprovalDropdownObj,
	trimSupplierDropdowns,
	trimUnitDropdowns,
} from "../../data";
import SampleRowModal from "../SampleRowModal/SampleRowModal";

const createRowKey = () => `${Date.now()}_${Math.random().toString(36).slice(2, 9)}`;

const cloneSampleRow = (row = {}, fallbackKey) => ({
	...row,
	__rowKey: row.__rowKey ?? row.id ?? fallbackKey ?? createRowKey(),
});

const stripSampleClientFields = (row = {}) => {
	const { __rowKey, __isNew, __isDeleted, is_new, is_updated, createdAt, updatedAt, ...rest } = row;
	return rest;
};

const SupplierRowModal = ({ open, setOpen, mode = "create", selectedRow, onSave = () => {}, selectedTrim = {} }) => {
	const { snackBar, setSnackBarOpen, setSnackBar } = GlobalState();
	const defPageData = Object.values(trimsSupplierColumnsObj)
		.map((col) => ({ [col.key]: getColDefaultValue(col) }))
		.reduce((acc, row) => ({ ...acc, ...row }), {});

	const [pageData, setPageData] = useState(defPageData);
	const [errorObj, setErrorObj] = useState({});
	const [sampleRows, setSampleRows] = useState([]);
	const [deletedSampleRows, setDeletedSampleRows] = useState([]);
	const [sampleModalOpen, setSampleModalOpen] = useState(false);
	const [sampleModalMode, setSampleModalMode] = useState("create");
	const [selectedSampleRow, setSelectedSampleRow] = useState(null);

	useEffect(() => {
		if (!open) return;

		if (mode === "update" && selectedRow) {
			setPageData(selectedRow);
			const nextSamples = Array.isArray(selectedRow?.styleTrimsSupplierSamples)
				? selectedRow.styleTrimsSupplierSamples.map((row, index) => cloneSampleRow(row, `${index}_${row?.id ?? createRowKey()}`))
				: [];
			setSampleRows(nextSamples);
			setDeletedSampleRows([]);
			return;
		}

		setPageData(defPageData);
		setSampleRows([]);
		setDeletedSampleRows([]);
	}, [selectedRow, mode, open]);

	useEffect(() => {
		const derivedApproval = deriveSupplierDesignerApprovalFromSamples(sampleRows);
		setPageData((prev) => ({ ...prev, ...derivedApproval }));
	}, [sampleRows]);

	const designerApprovalTone =
		pageData?.[trimsSupplierColumnsObj.designer_approval.key] === trimDesignerApprovalDropdownObj.APPROVED.key
			? "success"
			: pageData?.[trimsSupplierColumnsObj.designer_approval.key] === trimDesignerApprovalDropdownObj.REJECTED.key
			  ? "error"
			  : "default";

	const handleClose = () => {
		setOpen(false);
		setPageData(defPageData);
		setSampleRows([]);
		setDeletedSampleRows([]);
		setSelectedSampleRow(null);
		setSampleModalOpen(false);
		setErrorObj({});
	};

	const buildSamplePayloads = () => {
		if (mode === "create" || !selectedRow?.trim_supplier_code) {
			return {
				styleTrimsSupplierSamples: sampleRows.map((row) => stripSampleClientFields(row)),
			};
		}

		const createRows = sampleRows
			.filter((row) => row.is_new)
			.map((row) => {
				const { id, is_new, __rowKey, ...rest } = stripSampleClientFields(row);
				return rest;
			});

		const updateRows = sampleRows
			.filter((row) => !row.is_new && row.is_updated)
			.map((row) => {
				const { is_updated, __rowKey, ...rest } = stripSampleClientFields(row);
				return rest;
			});

		const deleteRows = deletedSampleRows.map((row) => stripSampleClientFields(row));

		return {
			styleTrimsSupplierSamplesToCreate: createRows,
			styleTrimsSupplierSamplesToUpdate: updateRows,
			styleTrimsSupplierSamplesToDelete: deleteRows,
		};
	};

	const handleSampleSave = (rowPayload) => {
		const nextRow = {
			...rowPayload,
			__rowKey: rowPayload?.__rowKey ?? rowPayload?.id ?? createRowKey(),
		};

		setSampleRows((prev) => {
			const index = prev.findIndex((row) => row.__rowKey === nextRow.__rowKey || (row.id && row.id === nextRow.id));
			if (index >= 0) {
				const existing = prev[index];
				const copy = [...prev];
				copy[index] = {
					...existing,
					...nextRow,
					...(existing.is_new ? {} : { is_updated: true }),
				};
				return copy;
			}
			return [...prev, { ...nextRow, is_new: !nextRow?.id }];
		});
		setSelectedSampleRow(null);
		setSampleModalOpen(false);
	};

	const handleDeleteSampleRow = (row) => {
		if (!row.is_new && row.id) {
			setDeletedSampleRows((prev) => [...prev, stripSampleClientFields(row)]);
		}
		setSampleRows((prev) => prev.filter((item) => item.__rowKey !== row.__rowKey));
	};

	const handleSave = () => {
		const hasAnyValue = Object.entries(pageData).some(([key, value]) => {
			if (key === trimsSupplierColumnsObj.unit_price.key) return Number(value) > 0;
			return Array.isArray(value) ? value.length : value !== "" && value !== null && value !== undefined;
		});

		if (!hasAnyValue) {
			setSnackBarOpen(true);
			setSnackBar({
				...snackBar,
				severity: "warning",
				message: "Please fill at least one supplier field",
			});
			return;
		}

		let errObj = {};
		Object.values(trimsSupplierColumnsObj).forEach((col) => {
			if (col.input_type === inputTypes.AUTO_GENERATE.key) return;
			if (col.constraints?.NOTNULL && !pageData[col.key]) {
				errObj = { ...errObj, [col.key]: `${col.name} is required` };
			}
		});

		if (Object.keys(errObj).length) {
			setSnackBarOpen(true);
			setSnackBar({
				...snackBar,
				severity: "warning",
				message: "Please fill the required fields",
			});
			setErrorObj(errObj);
			return;
		}

		const derivedApproval = deriveSupplierDesignerApprovalFromSamples(sampleRows);
		onSave({
			...pageData,
			...derivedApproval,
			...buildSamplePayloads(),
		});
		handleClose();
	};

	const localSupplierColumn = useMemo(() => {
		if (!selectedTrim?.[trimCategoryColumn.key]) return trimsSupplierColumnsObj.supplier;

		return {
			...trimsSupplierColumnsObj.supplier,
			[selectOptionsKeyName]: {
				type: arrType,
				list: trimSupplierDropdowns.filter((item) => item[trimCategoryColumn.key] === selectedTrim[trimCategoryColumn.key]),
			},
		};
	}, [selectedTrim?.[trimCategoryColumn.key]]);

	const localUnitColumn = useMemo(() => {
		if (!selectedTrim?.[trimCategoryColumn.key]) return trimsSupplierColumnsObj.unit;

		return {
			...trimsSupplierColumnsObj.unit,
			[selectOptionsKeyName]: {
				type: arrType,
				list: trimUnitDropdowns.filter((item) => item[trimCategoryColumn.key] === selectedTrim[trimCategoryColumn.key]),
			},
		};
	}, [selectedTrim?.[trimCategoryColumn.key]]);

	const localCurrencyColumn = {
		...trimsSupplierColumnsObj.currency,
		[selectOptionsKeyName]: {
			type: arrType,
			list: trimCurrencyDropdowns,
		},
	};

	const isTrimPublished = selectedTrim?.[trimsMasterColumnsObj.status.key] === statusOptions.PUBLISHED.key;

	return (
		<Dialog fullWidth maxWidth="md" open={open} onClose={handleClose} scroll="body">
			<Stack spacing={2} p={2}>
				<Typography variant="h6">{mode === "create" ? "Add" : "Update"} Supplier Details</Typography>
				<Box display="flex" flexWrap="wrap" gap="3%">
					<RenderInputField
						column={localSupplierColumn}
						data={pageData}
						setData={setPageData}
						width="30%"
						error={errorObj[localSupplierColumn.key]}
						selectOptObjName={selectOptionsKeyName}
					/>
					<RenderInputField
						column={trimsSupplierColumnsObj.supplier_reference_code}
						data={pageData}
						setData={setPageData}
						width="30%"
						error={errorObj[trimsSupplierColumnsObj.supplier_reference_code.key]}
					/>
					<Box width="30%" />

					<RenderInputField
						column={localUnitColumn}
						selectOptObjName={selectOptionsKeyName}
						data={pageData}
						setData={setPageData}
						width="30%"
						error={errorObj[localUnitColumn.key]}
					/>
					<RenderInputField
						column={trimsSupplierColumnsObj.unit_price}
						data={pageData}
						setData={setPageData}
						width="30%"
						error={errorObj[trimsSupplierColumnsObj.unit_price.key]}
					/>
					<RenderInputField
						column={localCurrencyColumn}
						selectOptObjName={selectOptionsKeyName}
						data={pageData}
						setData={setPageData}
						width="30%"
						error={errorObj[localCurrencyColumn.key]}
					/>
					<RenderInputField
						column={trimsSupplierColumnsObj.remarks}
						data={pageData}
						setData={setPageData}
						width="30%"
						error={errorObj[trimsSupplierColumnsObj.remarks.key]}
					/>

					<Box
						sx={{
							width: "100%",
							mt: 1,
							p: 1.25,
							borderRadius: 2,
							bgcolor: grey[50],
							border: `1px solid ${grey[200]}`,
						}}>
						<Typography variant="body2" fontWeight={700} mb={1}>
							Designer Approval (auto from latest sample)
						</Typography>
						<Box display="flex" flexWrap="wrap" gap={2} alignItems="center">
							<Chip
								size="small"
								label={pageData?.[trimsSupplierColumnsObj.designer_approval.key] || "PENDING"}
								color={designerApprovalTone}
								sx={{ fontWeight: 700 }}
							/>
							<Typography variant="body2" color={grey[700]}>
								Approved By: {pageData?.[trimsSupplierColumnsObj.approved_by.key] || "----"}
							</Typography>
							<Typography variant="body2" color={grey[700]}>
								Date of Approval: {pageData?.[trimsSupplierColumnsObj.date_of_approval.key] || "----"}
							</Typography>
						</Box>
					</Box>

					<Box
						sx={{
							p: 1,
							borderRadius: 2,
							bgcolor: pageData?.[trimsSupplierColumnsObj.supplier_active_status.key] === "ACTIVE" ? "#eef8f1" : "#fafafa",
							border: `1px solid ${
								pageData?.[trimsSupplierColumnsObj.supplier_active_status.key] === "ACTIVE" ? "#cfead6" : "#e5e7eb"
							}`,
							width: "40%",
							mt: 2,
							display: "flex",
							justifyContent: "space-between",
						}}>
						<Box display="flex" alignItems="center">
							<Typography variant="body2" fontWeight={700}>
								Active Status
							</Typography>
							<LightTooltip title="Supplier active status controls whether this supplier row is available for use.">
								<IconButton size="small" color="primary">
									<InfoOutlined fontSize="small" />
								</IconButton>
							</LightTooltip>
						</Box>
						<Box display="flex" alignItems="center" gap={1}>
							<Chip
								size="small"
								label={pageData?.[trimsSupplierColumnsObj.supplier_active_status.key] || "INACTIVE"}
								color={pageData?.[trimsSupplierColumnsObj.supplier_active_status.key] === "ACTIVE" ? "success" : "default"}
								sx={{ fontWeight: 700 }}
							/>
							<Switch
								checked={pageData?.[trimsSupplierColumnsObj.supplier_active_status.key] === "ACTIVE"}
								onChange={(e) =>
									setPageData((prev) => ({
										...prev,
										[trimsSupplierColumnsObj.supplier_active_status.key]: e.target.checked ? "ACTIVE" : "INACTIVE",
									}))
								}
								color="success"
								disabled={!isTrimPublished}
							/>
						</Box>
						{!isTrimPublished && (
							<Typography variant="caption" color="text.secondary">
								Publish the trim first to enable supplier active status.
							</Typography>
						)}
					</Box>
				</Box>

				<Divider />

				<Box>
					<Box display="flex" alignItems="center" justifyContent="space-between" mb={1}>
						<Box>
							<Typography variant="subtitle1" fontWeight={800}>
								Samples
							</Typography>
							<Typography variant="body2" color={grey[600]}>
								Add one or more sample records for this supplier.
							</Typography>
						</Box>
						<Button
							variant="outlined"
							size="small"
							startIcon={<AddRounded />}
							onClick={() => {
								setSelectedSampleRow(null);
								setSampleModalMode("create");
								setSampleModalOpen(true);
							}}>
							Add Sample
						</Button>
					</Box>

					<TableContainer component={Paper} variant="outlined">
						<Table size="small">
							<TableHead>
								<TableRow>
									<TableCell sx={{ fontWeight: 700 }}>Sample ETD</TableCell>
									<TableCell sx={{ fontWeight: 700 }}>Delivery Status</TableCell>
									<TableCell sx={{ fontWeight: 700 }}>Delivery Date</TableCell>
									<TableCell sx={{ fontWeight: 700 }}>Approval</TableCell>
									<TableCell sx={{ fontWeight: 700 }}>Remarks</TableCell>
									<TableCell sx={{ fontWeight: 700 }} align="center">
										Actions
									</TableCell>
								</TableRow>
							</TableHead>
							<TableBody>
								{sampleRows.length ? (
									sampleRows.map((row) => (
										<TableRow key={row.__rowKey} hover>
											<TableCell>{row.sample_etd_date || "-"}</TableCell>
											<TableCell>{row.sample_delivery_status || "-"}</TableCell>
											<TableCell>{row.sample_delivery_date || "-"}</TableCell>
											<TableCell>{row.approval || "-"}</TableCell>
											<TableCell>{row.remarks || "-"}</TableCell>
											<TableCell align="center">
												<IconButton
													size="small"
													onClick={() => {
														setSelectedSampleRow(row);
														setSampleModalMode("update");
														setSampleModalOpen(true);
													}}>
													<EditRounded fontSize="small" />
												</IconButton>
												<IconButton size="small" color="error" onClick={() => handleDeleteSampleRow(row)}>
													<DeleteRounded fontSize="small" />
												</IconButton>
											</TableCell>
										</TableRow>
									))
								) : (
									<TableRow>
										<TableCell colSpan={6} align="center">
											No samples added yet.
										</TableCell>
									</TableRow>
								)}
							</TableBody>
						</Table>
					</TableContainer>
				</Box>
			</Stack>
			<DialogActions>
				<Button variant="outlined" onClick={handleClose}>
					Cancel
				</Button>
				<Button variant="contained" onClick={handleSave}>
					Save
				</Button>
			</DialogActions>

			{sampleModalOpen && (
				<SampleRowModal
					open={sampleModalOpen}
					setOpen={setSampleModalOpen}
					mode={sampleModalMode}
					selectedRow={selectedSampleRow}
					selectedTrim={selectedTrim}
					onSave={handleSampleSave}
				/>
			)}
		</Dialog>
	);
};

export default SupplierRowModal;
